import { Component, forwardRef, Input, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Material search input. Use `matchesSearchBarQuery` / `filterBySearchBarQuery`
 * from `search-bar-field.utils.ts` when filtering lists so behavior stays aligned.
 */
@Component({
  selector: 'ra-search-bar-field',
  templateUrl: './search-bar-field.component.html',
  styleUrls: ['./search-bar-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchBarFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SearchBarFieldComponent implements ControlValueAccessor {
  /** Mat-form-field label (e.g. from JSON config). */
  @Input() label = '';
  /** Input placeholder. */
  @Input() placeholder = '';
  /** Extra classes on `mat-form-field` (layout / typography). */
  @Input() formFieldClass = 'mx-2 mb-0 fs-small w-100';
  /** Suffix icon name (Material icon ligature). */
  @Input() suffixIcon = 'search';
  /**
   * Disables the control (e.g. no options yet). Merged with disabled state from
   * `FormControl` / `setDisabledState`.
   */
  @Input() disabled = false;

  value = '';
  private cvaDisabled = false;
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled;
  }

  writeValue(obj: string | null): void {
    this.value = obj ?? '';
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }

  onInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.value = v;
    this.onChange(v);
  }

  onBlur(): void {
    this.onTouched();
  }
}
